import React from "react";

/**
 * Phase 1 — HQBadge
 *
 * Unified corporate/admin context badge.
 *
 *   <HQBadge />                       → "HQ" (admin/corporate context, neutral)
 *   <HQBadge tone="brand">Melville</HQBadge>
 *                                     → store-scope chip (manager context)
 *
 * Permanent rule: this is the ONLY way to render the corporate/admin
 * context chip. It always sits in the top-right of the page header
 * (via <PageHeader />), never inline in body text.
 *
 * Frontend display only — does NOT rename stores or modify any
 * database / store-id state. The literal string "Main Office" in the
 * UI becomes "HQ"; the underlying store record name is untouched.
 */
export default function HQBadge({
  children = "HQ",
  tone = "neutral",
  className = "",
  "data-testid": testId = "hq-badge",
  ...rest
}) {
  const dotClass =
    tone === "brand" ? "bg-brand" : "bg-ink/40";
  const wrapClass =
    tone === "brand"
      ? "bg-brand-tint border border-brand/20 text-ink"
      : "bg-paper-sunk border border-border/60 text-ink/80";

  return (
    <span
      className={`inline-flex items-center gap-2 h-7 px-3 rounded-chip font-mono text-[11px] uppercase tracking-wider ${wrapClass} ${className}`}
      data-testid={testId}
      {...rest}
    >
      <span
        aria-hidden="true"
        className={`inline-block w-1.5 h-1.5 rounded-full ${dotClass}`}
      />
      <span className="font-bold">{children}</span>
    </span>
  );
}
